import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { ArrowLeft, Clock } from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { dummyProfilingData } from './dummyProfilingData';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import heartLoading from "../../../assests/heartLoading.gif"
import preloader from "../../../assests/preloader1.gif"
import Plot from 'react-plotly.js';
import LoadingScreen from './LoadingScreen';



// A simple component for the horizontal bar chart
const DataBarChart = ({ data }) => {
    if (!Array.isArray(data) || data.length === 0) {
        return <Typography variant="caption" color="text.secondary">No data</Typography>;
    }
    const maxValue = Math.max(...data.map(d => d.value));
    return (
        <Box sx={{ width: '100%', pr: 2 }}>
            {data.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ width: '80px', textAlign: 'right', mr: 1 }}>{item.label}</Typography>
                    <Box sx={{ flexGrow: 1, bgcolor: '#e0e0e0', borderRadius: '4px' }}>
                        <Box sx={{
                            width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`,
                            bgcolor: '#1976d2',
                            height: '16px',
                            borderRadius: '4px',
                            transition: 'width 0.5s ease-in-out'
                        }} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};



const DataProfiling = ({ onPrevious, nextStep, id }) => {
    const [profilingStatusLoading, setProfilingStatusLoading] = useState(false);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [profilingResults, setProfilingResults] = useState([]);
    const [businessTerms, setBusinessTerms] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [popupAssetName, setPopupAssetName] = useState("")
    const [profilingResultStatus, setProfilingResultStatus] = useState("")
    const [currentRowCount,  setCurrentRowCount] = useState("")
    const [graphOpen, setGraphOpen] = useState(false);
    const [graphColumn, setGraphColumn] = useState(null);
    const [error, setError] = useState("");
    const [profilingJobStatus, setProfilingJobStatus] = useState([]);
    const [totalFiles, setTotalFiles] = useState(0);
    const [completedFiles, setCompletedFiles] = useState(0);
    const [finalResult, setFinalResult] = useState(false);
    const [isReRunning, setIsReRunning] = useState(false);
    const isFetchingResults = useRef(false);
    console.log(
        "current row count", currentRowCount
    )
    // console.log("selected results", selectedResult)

    // console.log("profiling Results", profilingResults)

    // --- Loading Progress State ---
    const [progress, setProgress] = useState(0);
    const [currentPhase, setCurrentPhase] = useState('Initializing');
    const totalExpectedTime = 15 * 60; // 15 minutes in seconds
    const initialPhaseTime = 20; // seconds for first 3%
    const phases = [
        { name: 'Initializing', maxProgress: 5 },
        { name: 'Scanning data sources', maxProgress: 15 },
        { name: 'Analyzing data structure', maxProgress: 35 },
        { name: 'Profiling data quality', maxProgress: 55 },
        { name: 'Generating insights', maxProgress: 75 },
        { name: 'Finalizing analysis', maxProgress: 85 }
    ];
    useEffect(() => {
        if (!profilingStatusLoading) return;
        let start = Date.now();
        let animation;
        function animate() {
            const elapsed = (Date.now() - start) / 1000; // seconds
            let pct;
            if (elapsed < initialPhaseTime) {
                pct = (elapsed / initialPhaseTime) * 3; // 0-3% in first 20s
            } else {
                const remainingTime = totalExpectedTime - initialPhaseTime;
                const elapsedAfterInitial = elapsed - initialPhaseTime;
                pct = 3 + ((elapsedAfterInitial / remainingTime) * (85 - 3));
            }
            pct = Math.min(pct, 85);
            setProgress(pct);
            // Set phase
            const phase = phases.find(ph => pct <= ph.maxProgress) || phases[phases.length - 1];
            setCurrentPhase(phase.name);
            if (pct < 85) {
                animation = requestAnimationFrame(animate);
            }
        }
        animation = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animation);
    }, [profilingStatusLoading]);
    // Calculate remaining time
    const remainingSeconds = Math.max(0, Math.round(((85 - progress) / 85) * totalExpectedTime));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    // --- END Loading Progress State ---

        const fetchResults = async () => {
        // Prevent duplicate calls
        if (isFetchingResults.current) {
            return;
        }
        
        isFetchingResults.current = true;
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/profiling_results/${id}`);
                if (response?.status === 200 || response?.status === 201) {
                    setProfilingResults(response?.data?.profiling_results);
                setFinalResult(true);
                toast.success("Profiling results loaded successfully!");
                }
            } catch (err) {
                setError("Failed to load profiling results.");
                toast.error("Failed to load profiling results.");
        } finally {
            isFetchingResults.current = false;
            }
        };

        const pollStatus = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-profiling-job-status/${id}`);
            
            // Update profiling job status
            if (response?.data?.profiling_job_status) {
                setProfilingJobStatus(response.data.profiling_job_status);
                setTotalFiles(response.data.profiling_job_status.length);
                
                // Count completed files
                const completed = response.data.profiling_job_status.filter(
                    job => job.status === "Completed"
                ).length;
                setCompletedFiles(completed);
            }
            
                if (response?.data?.overall_status === "Completed") {
                    setProfilingResultStatus("Completed");
                    setProfilingStatusLoading(false);
                // Fetch results once when completed
                if (!finalResult) {
                    setResultsLoading(true);
                    await fetchResults();
                    setResultsLoading(false);
                }
                } else if (response?.data?.overall_status === "In Progress" || response?.data?.overall_status === "Pending") {
                    setProfilingResultStatus(response?.data?.overall_status);
                    setProfilingStatusLoading(true);
                // Continue polling every 60 seconds
                setTimeout(pollStatus, 60000);
                } else if (response?.data?.overall_status === "Failed") {
                    setProfilingResultStatus("Failed");
                    setProfilingStatusLoading(false);
                    setError("Profiling job failed.");
                }
            } catch (err) {
                setProfilingStatusLoading(false);
                setError("Failed to poll profiling status.");
            }
        };

    const handleBusinessTermChange = (assetName, columnName, value) => {
        setBusinessTerms(prev => ({
            ...prev,
            [`${assetName}-${columnName}`]: value
        }));
    };

    const areAllBusinessTermsFilled = () => {
        return Object.values(businessTerms).every(term => term.trim() !== '');
    };

    const handleSaveDraft = async () => {
        setIsSaving(true);
        try {
            const payload = profilingResults.map(asset => ({
                assetName: asset.assetName,
                columns: asset.columns.map(col => ({
                    columnName: col.columnName,
                    dataType: col.dataType,
                    businessTerm: businessTerms[`${asset.assetName}-${col.columnName}`] || '',
                    nullPercentage: col.nullPercentage,
                    uniquePercentage: col.uniquePercentage,
                    distribution: col.dataDistribution
                }))
            }));
            await axios.post(`${process.env.REACT_APP_API_URL}/save-profiling-draft`, payload);
            toast.success('Draft saved successfully!');
        } catch (error) {
            console.error('Error saving draft:', error);
            toast.error('Failed to save draft. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReadyForDQ = async () => {
        if (!areAllBusinessTermsFilled()) {
            toast.error('Please fill in all business terms before proceeding.');
            return;
        }
        // Build payload
        const payload = {ProducId : id}
        try {
            // Call rule init API
            // const ruleResponse = await axios.post(`${process.env.REACT_APP_API_URL}/rule/init/${id}`);
            // if (ruleResponse.status === 200 || ruleResponse.status === 201) {
            //     // console.log('Rule initialization successful');
            // }
            
            // Call DQ start API
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/dataQuality/start/${id}`);
        if(response.status === 200 || 201){
                toast.success("Rule initialization successful");
        nextStep();
        }
        } catch (error) {
            console.error('DQ Checks API failed (expected):', error);
            toast.error('DQ Checks API failed (but proceeding)...');
        }
    };

    const handleReRunProfiling = async () => {
        setIsReRunning(true);
        setError("");
        try {
            // Trigger profiling API
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/trigger_profiling/${id}`);
            if (response.status === 200 || response.status === 201) {
                toast.success("Profiling restarted successfully!");
                // Wait 2 seconds before starting polling
                setTimeout(() => {
                    setIsReRunning(false);
                    setProfilingStatusLoading(true);
                    setProfilingResultStatus("Pending");
                    setFinalResult(false);
                    setProfilingResults([]);
                    setCompletedFiles(0);
                    setTotalFiles(0);
                    // Trigger the polling flow by calling pollStatus
                    pollStatus();
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to restart profiling:', error);
            toast.error('Failed to restart profiling. Please try again.');
            setIsReRunning(false);
        }
    };

    useEffect(() => {
        // Start polling only if not re-running
        if (!isReRunning) {
            pollStatus();
        }
    }, [id, isReRunning]);

    if (profilingStatusLoading) {
        return (
            <Paper elevation={3} sx={{ p: 4, maxWidth: 700, margin: 'auto', mt: 16, border: "1px solid #dacdcd" }}>
                <Box sx={{ textAlign: 'center' }}>
                    {/* <Clock size={48} className="text-[#0000a0] animate-pulse block m-auto" />  */}
                    <img src={preloader} alt='loading' width={200} className='block m-auto animate-pulse'/>
                    <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
                        Profiling in Progress
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {completedFiles}/{totalFiles} Completed
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Please wait while we analyze your data assets. This might take a few moments.
                    </Typography>
                    <LinearProgress />
                </Box>
            </Paper>
        );
    }
    if (resultsLoading) {
        return (
            <Paper elevation={3} sx={{ p: 4, maxWidth: 700, margin: 'auto', mt: 16, border: "1px solid #dacdcd" }}>
                <Box sx={{ textAlign: 'center' }}>
                    <img src={preloader} alt='loading' width={200} className='block m-auto animate-pulse'/>
                    <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
                        Loading Results...
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Please wait while we load your profiling results.
                    </Typography>
                    <LinearProgress />
                </Box>
            </Paper>
        );
    }
    if (error) {
        return (
            <Paper elevation={3} sx={{ p: 4, maxWidth: 700, margin: 'auto', mt: 16, border: "1px solid #dacdcd" }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="error" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
                        Profiling Failed
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        {error}
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={handleReRunProfiling}
                        disabled={isReRunning}
                        sx={{ 
                            backgroundColor: '#0000a0',
                            '&:hover': {
                                backgroundColor: '#000080'
                            },
                            px: 4,
                            py: 1.5,
                            borderRadius: 2
                        }}
                    >
                        {isReRunning ? 'Restarting...' : 'Re-run Profiling'}
                    </Button>
                </Box>
            </Paper>
        );
    }

    return (
        <Box sx={{ maxWidth: '90%', margin: 'auto', mt: 4 ,  }} className = "  rounded pb-4 " >
            {finalResult && (
                <>
            <Box sx={{ mb: 4, p: 3 }}>
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{
                        color: '#0000a0',
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Box
                        sx={{
                            width: '4px',
                            height: '24px',
                            backgroundColor: '#0000a0',
                            borderRadius: '2px'
                        }}
                    />
                    Profiling Results
                </Typography>
            </Box>
            <Box sx={{ px: 4, pb: 4 }}>
                {profilingResults.map((result, index) => (
                    <Box
                        key={result.data_asset_name}
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            padding: 3,
                            marginBottom: 2,
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#2c3e50',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {result.data_asset_name}
                                </Typography>
                            </Box>
                            <Button 
                                variant="contained"
                                sx={{ 
                                    backgroundColor: '#0000a0',
                                    color: 'white',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontSize: '0.9rem',
                                    '&:hover': {
                                        backgroundColor: '#000080'
                                    }
                                }}
                                onClick={() => { 
                                    setSelectedResult(result?.columns); 
                                    setModalOpen(true); 
                                    setPopupAssetName(result.data_asset_name); 
                                    setCurrentRowCount(result?.columns[0]?.count) 
                                }}
                            >
                                View Result
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2, mr: 4 }}>
                {/* <Button 
                    variant="outlined" 
                    onClick={handleSaveDraft}
                    disabled={isSaving}
                    sx={{ color : "#0000a0", border: '1px solid #0000a0' }}
                >
                    {isSaving ? 'Saving...' : 'Save Draft'}
                </Button> */}
                <Button 
                    variant="contained" 
                    sx={{backgroundColor: '#0000a0'}}
                    onClick={handleReadyForDQ}
                    // disabled={!areAllBusinessTermsFilled()}
                >
                    Ready to Run DQ Checks
                </Button>
            </Box>
                </>
            )}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="lg" fullWidth>
                <div className='flex justify-between items-center '>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>{popupAssetName}</DialogTitle>
                
                <IconButton sx={{marginRight: '20px'}} onClick={() => setModalOpen(false) } >
                <CloseIcon />
                </IconButton>
                </div>
                <DialogContent>
                    {/* <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>{selectedResult?.assetPath || selectedResult?.assetFullPath || selectedResult?.assetTable || ''}</Typography> */}
                    <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                padding: 3,
                                width: '200px',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                border: '1px solid #d3d3d3',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                                    transform: 'translateY(-6px) !important',
                                    border: '1px solid #0000a0 !important',
                                    backgroundColor: '#f8f9ff'
                                }
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                                >
                                    Columns Analyzed
                                </Typography>
                                <Box
                                    sx={{
                                        height: '3px',
                                        width: '40px',
                                        backgroundColor: '#0000a0',
                                        borderRadius: '2px',
                                        mt: 0.5,
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
                            >
                                {selectedResult?.length || 0}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                padding: 3,
                                width: '200px',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                border: '1px solid #d3d3d3',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                                    transform: 'translateY(-6px) !important',
                                    border: '1px solid #0000a0 !important',
                                    backgroundColor: '#f8f9ff'
                                }
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                                >
                                    Rows Profiled
                                </Typography>
                                <Box
                                    sx={{
                                        height: '3px',
                                        width: '40px',
                                        backgroundColor: '#0000a0',
                                        borderRadius: '2px',
                                        mt: 0.5,
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
                            >
                                {currentRowCount || 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '12px',
                            marginTop: "20px",
                            maxHeight: '500px',
                            overflow: 'auto'
                        }}
                    >
                        <Table sx={{ minWidth: 650 }} stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Column
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Data Type
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        CDE
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Business Term
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Null %
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Statistics
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedResult?.map((col, index) => (
                                    <TableRow 
                                        key={col.column_name}
                                        sx={{ 
                                            backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                                            '&:hover': {
                                                backgroundColor: '#f0f8ff',
                                                transition: 'background-color 0.2s ease'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#2c3e50'
                                        }}>
                                            {col.column_name}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            {col.type}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px'
                                        }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    backgroundColor: col.isCde ? '#e8f5e8' : '#fff3e0',
                                                    color: col.isCde ? '#2e7d32' : '#f57c00'
                                                }}
                                            >
                                                {col.isCde ? "Yes" : "No"}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            {col.businessterm === null ? "N/A" : col.businessterm}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            {col.nullPercentage === null ? "N/A" : `${col.nullPercentage || 0}%`}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <span><strong>Mean:</strong> {col.mean === null ? "N/A" : col.mean}</span>
                                                <span><strong>Max:</strong> {col.max === null ? "N/A" : col.max}</span>
                                                <span><strong>Min:</strong> {col.min === null ? "N/A" : col.min}</span>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px'
                                        }}>
                                            <Button 
                                                variant="text" 
                                                size="small" 
                                                sx={{ 
                                                    color: '#0000a0', 
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        backgroundColor: '#f0f8ff'
                                                    }
                                                }} 
                                                onClick={() => { setGraphOpen(true); setGraphColumn(col); }}
                                            >
                                                View Plot
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
            {/* Plotly Graph Popup */}
            <Dialog open={graphOpen} onClose={() => setGraphOpen(false)} maxWidth="md" fullWidth>
                <div className='flex justify-end items-center p-4'>
                    <IconButton onClick={() => setGraphOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <DialogContent sx={{ p: 0 }}>
                    <Plot
                        data={graphColumn?.chart?.data || []}
                        layout={{
                            ...(graphColumn?.chart?.layout || {}),
                            margin: { t: 60, b: 80, ...(graphColumn?.chart?.layout?.margin || {}) },
                            autosize: true,
                            width: null,
                            height: 500
                        }}
                        style={{ width: '100%', height: '100%' }}
                        useResizeHandler={true}
                    />
                </DialogContent>
            </Dialog>
            <Toaster position="top-right" />
        </Box>
    );
};

export default DataProfiling;